import AuthService from './AuthService.js'
import CacheService from './CacheService.js'
import { eventBus } from '@/utils/eventBus'

// Debounce pour les sauvegardes fréquentes
let saveTimeout = null
const SAVE_DEBOUNCE_DELAY = 1000 // 1 seconde

/**
 * Service pour gérer la progression des tutoriels via l'API backend
 * Optimisé avec cache et debouncing pour réduire les appels API
 */
const TutorialService = {
  /**
   * Récupère la progression actuelle des tutoriels de l'utilisateur
   * Auto-crée un enregistrement avec des valeurs par défaut si aucun n'existe
   * Utilise le cache pour éviter les appels répétés
   * @param {boolean} forceRefresh - Force le rafraîchissement depuis l'API
   * @returns {Promise<Object>} - La progression complète des tutoriels
   */
  async getProgress(forceRefresh = false) {
    try {
      if (forceRefresh) {
        CacheService.invalidate('tutorialProgress')
      }

      return await CacheService.getOrFetch('tutorialProgress', async () => {
        const response = await AuthService.fetchWithAuth({
          method: 'get',
          url: '/user/tutorial/progress',
        })
        return response.data
      })
    } catch (error) {
      console.error('Erreur lors de la récupération de la progression:', error)
      throw error
    }
  },

  /**
   * Met à jour la progression du tutoriel du dashboard
   * Utilise le debouncing pour éviter trop de sauvegardes consécutives
   * @param {Object} data - Les données à mettre à jour
   * @param {boolean} [data.completed] - Si le tutoriel est terminé
   * @param {boolean} [data.visited] - Si le dashboard a été visité
   * @param {number} [data.tourStep] - L'étape actuelle du tour guidé
   * @param {boolean} [data.forceShow] - Force l'affichage du guide
   * @param {boolean} [data.highlightAvatar] - Active la surbrillance de l'avatar
   * @param {boolean} [immediate=false] - Sauvegarde immédiate sans debouncing
   * @returns {Promise<Object>} - La progression mise à jour
   */
  async updateDashboardProgress(data, immediate = false) {
    if (!immediate) {
      // Debouncing: attendre un peu avant de sauvegarder
      return new Promise((resolve, reject) => {
        if (saveTimeout) clearTimeout(saveTimeout)

        saveTimeout = setTimeout(async () => {
          try {
            const result = await this._saveDashboardProgress(data)
            resolve(result)
          } catch (error) {
            reject(error)
          }
        }, SAVE_DEBOUNCE_DELAY)
      })
    }

    return this._saveDashboardProgress(data)
  },

  async _saveDashboardProgress(data) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'patch',
        url: '/user/tutorial/dashboard',
        data: data,
      })

      // Invalider le cache et émettre un événement
      CacheService.invalidate('tutorialProgress')
      eventBus.emit('tutorial-progress-updated', { type: 'dashboard', data: response.data })

      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour du dashboard:', error)
      throw error
    }
  },

  /**
   * Met à jour la progression du tutoriel du profil
   * @param {Object} data - Les données à mettre à jour
   * @param {boolean} [data.completed] - Si le tutoriel du profil est terminé
   * @param {boolean} [data.visited] - Si le profil a été visité
   * @param {boolean} [data.active] - Si le tour du profil est actif
   * @param {number} [data.currentStep] - L'étape actuelle du tour du profil
   * @param {number[]} [data.visitedSections] - Les sections visitées
   * @returns {Promise<Object>} - La progression mise à jour
   */
  async updateProfileProgress(data) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'patch',
        url: '/user/tutorial/profile',
        data: data,
      })

      // Invalider le cache et émettre un événement
      CacheService.invalidate('tutorialProgress')
      eventBus.emit('tutorial-progress-updated', { type: 'profile', data: response.data })

      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error)
      throw error
    }
  },

  /**
   * Marque une section du profil comme visitée
   * @param {number} sectionId - L'ID de la section visitée
   * @returns {Promise<Object>} - La progression mise à jour
   */
  async markProfileSectionVisited(sectionId) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'post',
        url: '/user/tutorial/profile/sections/visited',
        data: { sectionId },
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors du marquage de la section:', error)
      throw error
    }
  },

  /**
   * Met à jour la progression du tutoriel de la page UserProfile
   * @param {Object} data - Les données à mettre à jour
   * @param {boolean} [data.completed] - Si le tutoriel UserProfile est terminé
   * @param {boolean} [data.visited] - Si la page UserProfile a été visitée
   * @param {boolean} [data.active] - Si le tour est actif
   * @param {number} [data.currentStep] - L'étape actuelle du tour
   * @returns {Promise<Object>} - La progression mise à jour
   */
  async updateUserProfileProgress(data) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'patch',
        url: '/user/tutorial/rewards',
        data: data,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour de UserProfile:', error)
      throw error
    }
  },

  /**
   * Met à jour les récompenses et badges
   * @param {Object} data - Les données de récompenses à mettre à jour
   * @param {Array} [data.userBadges] - La liste complète des badges
   * @param {number} [data.badgeClickCount] - Le nombre de clics sur le badge
   * @param {number} [data.lastEvolvedLevel] - Le dernier niveau évolué
   * @param {boolean} [data.badgeNeedsEvolution] - Si le badge doit évoluer
   * @returns {Promise<Object>} - La progression mise à jour
   */
  async updateRewards(data) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'patch',
        url: '/user/tutorial/rewards',
        data: data,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour des récompenses:', error)
      throw error
    }
  },

  /**
   * Migre les données de localStorage vers le backend
   * @param {Object} localStorageData - Les données à migrer
   * @returns {Promise<Object>} - La progression migrée
   */
  async migrateFromLocalStorage(localStorageData) {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'post',
        url: '/user/tutorial/migrate-localstorage',
        data: localStorageData,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la migration:', error)
      throw error
    }
  },

  /**
   * Réinitialise complètement la progression des tutoriels
   * @returns {Promise<Object>} - Confirmation de la réinitialisation
   */
  async resetProgress() {
    try {
      const response = await AuthService.fetchWithAuth({
        method: 'delete',
        url: '/user/tutorial/progress',
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error)
      throw error
    }
  },
}

export default TutorialService
